import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Building2, Users, MessageSquare } from 'lucide-react';
import { getSectors, createSector, updateSector, deleteSector } from '../services/api';

function Sectors() {
  const [sectors, setSectors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', whatsapp_number_id: '', login_start_time: '', login_end_time: '', alert_before_window_close_minutes: '', alert_unanswered_hours: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSectors();
  }, []);

  const loadSectors = async () => {
    try {
      const res = await getSectors();
      setSectors(res.data);
    } catch (err) {
      console.error('Erro ao carregar setores:', err);
      alert('Erro ao carregar setores');
    }
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    if (editing) {
      await updateSector(editing.id, form);
    } else {
      await createSector(form);
    }
    setShowModal(false);
    setEditing(null);
    setForm({ name: '', description: '', whatsapp_number_id: '', login_start_time: '', login_end_time: '', alert_before_window_close_minutes: '', alert_unanswered_hours: '' });
    loadSectors();
  } catch (err) {
    const errorData = err.response?.data;
    
    if (errorData?.details?.message) {
      alert(
        `⚠️ ${errorData.error}\n\n` +
        `${errorData.details.message}\n\n` +
        `Atual: ${errorData.details.current}/${errorData.details.max}\n` +
        `Plano: ${errorData.details.plan}`
      );
    } else {
      alert('Erro: ' + (errorData?.error || err.message));
    }
  } finally {
    setLoading(false);
  }
};

  const handleEdit = (sector) => {
    setEditing(sector);
    setForm({
      name: sector.name,
      description: sector.description || '',
      whatsapp_number_id: sector.whatsapp_number_id || '',
      login_start_time: sector.login_start_time ? sector.login_start_time.slice(0, 5) : '',
      login_end_time: sector.login_end_time ? sector.login_end_time.slice(0, 5) : '',
      alert_before_window_close_minutes: sector.alert_before_window_close_minutes || '',
      alert_unanswered_hours: sector.alert_unanswered_hours || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este setor?')) {
      try {
        await deleteSector(id);
        loadSectors();
      } catch (err) {
        alert('Erro ao excluir: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Estrutura Setorial</h1>
          <p className="text-gray-500">Organize atendentes e isolate dados por departamento</p>
        </div>
        <button 
          onClick={() => { 
            setEditing(null); 
            setForm({ name: '', description: '', whatsapp_number_id: '', login_start_time: '', login_end_time: '', alert_before_window_close_minutes: '', alert_unanswered_hours: '' }); 
            setShowModal(true); 
          }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100">
          <Plus size={18} /> Novo Setor
        </button>
      </div>

      {/* Grid de Setores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sectors.map(sector => (
          <div key={sector.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-green-50 text-green-600 rounded-xl">
                <Building2 size={24} />
              </div>
              <div className="flex gap-1">
                <button 
                  onClick={() => handleEdit(sector)} 
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Edit2 size={16} />
                </button>
                <button 
                  onClick={() => handleDelete(sector.id)} 
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-1">{sector.name}</h3>
            <p className="text-sm text-gray-500 mb-4 line-clamp-2">{sector.description || 'Sem descrição'}</p>
            <div className="flex items-center gap-4 pt-4 border-t border-gray-50 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Users size={14} /> {sector.users_count || 0} usuários
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare size={14} /> {sector.conversations_count || 0} conversas
              </span>
            </div>
          </div>
        ))}
        {sectors.length === 0 && (
          <p className="col-span-full text-center text-gray-400 py-12">Nenhum setor cadastrado.</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-bold mb-4">{editing ? 'Editar Setor' : 'Novo Setor'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Setor *</label>
                <input 
                  type="text" 
                  placeholder="Ex: Odonto, Médico, Exames" 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea 
                  placeholder="Descrição do setor" 
                  value={form.description} 
                  onChange={e => setForm({...form, description: e.target.value})} 
                  rows={3}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número WhatsApp (opcional)</label>
                <input 
                  type="text" 
                  placeholder="ID do número na Meta" 
                  value={form.whatsapp_number_id} 
                  onChange={e => setForm({...form, whatsapp_number_id: e.target.value})} 
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Horário Permitido para Login (opcional)</label>
                <p className="text-xs text-gray-400 mb-2">Bloqueia o login de atendentes deste setor fora do horário — deixe em branco para não restringir.</p>
                <div className="flex gap-3 items-center">
                  <input
                    type="time"
                    value={form.login_start_time}
                    onChange={e => setForm({...form, login_start_time: e.target.value})}
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  />
                  <span className="text-gray-400 text-sm">até</span>
                  <input
                    type="time"
                    value={form.login_end_time}
                    onChange={e => setForm({...form, login_end_time: e.target.value})}
                    className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alertar Admin — Mensagem Sem Resposta (opcional)</label>
                <p className="text-xs text-gray-400 mb-2">Avisa o admin da empresa quando uma mensagem do cliente demorar pra ser respondida, pra ele encaminhar pra um atendente. Deixe em branco pra não avisar.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Sem resposta há mais de (horas)</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Ex: 2"
                      value={form.alert_unanswered_hours}
                      onChange={e => setForm({...form, alert_unanswered_hours: e.target.value})}
                      className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Faltando p/ fechar janela 24h (min)</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Ex: 30"
                      value={form.alert_before_window_close_minutes}
                      onChange={e => setForm({...form, alert_before_window_close_minutes: e.target.value})}
                      className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50">
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sectors;